import Paper from "@mui/material/Paper";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Autocomplete,
    Box,
    Checkbox,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid2 as Grid,
    IconButton,
    InputLabel,
    ListItem,
    ListSubheader,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItemText from "@mui/material/ListItemText";
import React, {useEffect, useMemo, useState} from "react";
import productProjectService from "../../services/ProductProjectService";
import projectTaskService from "../../services/ProjectTaskService";
import {
    Add,
    AttachFile,
    Close,
    Delete,
    ExpandLess,
    ExpandMore,
    Launch,
    Remove,
    Settings,
    Star,
    StarHalf,
    StarOutline,
    UnfoldLess
} from "@mui/icons-material";
import projectService from "../../services/ProjectService";
import {useSnackbar} from "../../util/SnackbarProvider";
import {useNavigate} from "react-router-dom";
import {INVOICE_TYPES} from "../../util/Const";
import {parseToHUF} from "../../util/StringUtils";
import userService from "../../services/UserService";
import {EngineBarChart, EngineLineChart, EnginePieChart} from "./ChartEngine";

// localStorage kulcsok: kiválasztott projekt és accordion nyitott/zárt állapotok.
const LOCAL_STORAGE_NAME = "project-structure"
const ACCORDION_STATES = "project-structure-accordion-states"
const CHART_TEMPLATE_DEFAULTS = "project-structure-chart-template-defaults"

const ProjectStructure = () => {

    // Lista-szűrő: összes / aktuális / fél-aktuális projektek.
    const [relevanceFilter, setRelevanceFilter] = useState('ALL');
    // Korábban kiválasztott projekt visszatöltése, hogy frissítés után is megmaradjon.
    const [project, setProject] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem(LOCAL_STORAGE_NAME))
        } catch {
            return null
        }
    });

    // Projekt "Structure" API-ból betöltött fő adatszeletek.
    const [projectFiles, setProjectFiles] = useState([]);
    const [selectedProjectFiles, setSelectedProjectFiles] = useState([]);
    const [projectDetails, setProjectDetails] = useState([]);

    const [monthlyCosts, setMonthlyCosts] = useState([]);
    const [selectedProductProjects, setSelectedProductProjects] = useState([]);
    const [selectedProjectTasks, setSelectedProjectTasks] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [draftInvoices, setDraftInvoices] = useState([]);
    const {sendAlertMessage} = useSnackbar()
    const navigate = useNavigate();
    const [resetStatesButtonEnabled, setResetStatesButtonEnabled] = useState(false);

    // Minden panel (accordion) nyitott/zárt állapota perzisztálva localStorage-ban.
    const [accordionStates, setAccordionStates] = useState(JSON.parse(localStorage.getItem(ACCORDION_STATES)) || {});
    const flipState = (name) => {
        setAccordionStates(prev => {
            const next = {...prev, [name]: !prev[name]}
            localStorage.setItem(ACCORDION_STATES, JSON.stringify(next))
            return next;
        });
    };

    // Projekt státusz-opciók szerkesztő modal láthatósága.
    const [openStateSettings, setOpenStateSettings] = useState(false);
    const onSettingsClose = () => {
        setOpenStateSettings(false);
    }

    // Gyors "összes panel összecsukása".
    const collapseAll = () => {
        setAccordionStates({});
    }

    // Projekt váltáskor teljes struktúra újratöltése.
    useEffect(() => {
        retrieveProjectStructure()
    }, [project?.id]);

    // A központi betöltő függvény: ez tölti a statisztikát, fájlokat, feladatokat, számlákat.
    const retrieveProjectStructure = () => {
        if (project?.id == null) return
        projectService.findStructureById(project.id)
            .then(response => {
                setMonthlyCosts((response.monthlyCosts))
                setProjectDetails(response.details)
                setProjectFiles(response.files)
                setSelectedProjectTasks(response.projectTasks)
                setSelectedProductProjects(response.productProjects)
                setInvoices(response.invoices)
                setDraftInvoices(response.draftInvoices)
            })
            .catch(error => sendAlertMessage(error?.message || "Projekt lekérése sikertelen", 'error'));
    }

    // Selector adatok betöltése (projekt dropdown).
    const [projects, setProjects] = useState([]);
    useEffect(() => {
        projectService.getSelectors().then(setProjects).catch(console.error);
    }, []);

    // Dolgozó selector adatok (feladat-hozzárendelésekhez).
    const [users, setUsers] = useState([]);
    useEffect(() => {
        userService.getSelectors().then(setUsers).catch(console.error);
    }, []);

    // Konfigurálható projekt státusz-lista.
    const [projectStates, setProjectStates] = useState([]);
    useEffect(() => {
        retrieveProjectStates()
    }, []);

    const retrieveProjectStates = () => {
        projectService.getProjectStates()
            .then(response => {
                setProjectStates(response)
                setResetStatesButtonEnabled(false)
            })
            .catch(console.error);
    }

    // Státusz elem törlése a szerkesztő listából (még nem menti backendbe).
    const removeProjectStateAt = (index) => {
        setResetStatesButtonEnabled(true)
        setProjectStates(prev =>
            prev.filter((_, i) => i !== index)
        );
    };

    const addProjectState = () => {
        setResetStatesButtonEnabled(true)
        setProjectStates(prev => [
            ...prev,
            {id: null, name: ''}
        ]);
    };

    // Egy státusz sor mezőinek módosítása.
    const onProjectStateChange = (event, index) => {
        setResetStatesButtonEnabled(true)
        const value = event.target.value;
        const name = event.target.name;

        setProjectStates(prev =>
            prev.map((ps, i) => i === index ? {...ps, [name]: value} : ps)
        );
    };

    // Státusz-opciók mentése backendbe.
    const updateProjectStates = (e) => {
        e.preventDefault()
        projectService.updateProjectStates(projectStates)
            .then(() => {
                retrieveProjectStates()
                setResetStatesButtonEnabled(false)
                sendAlertMessage('Projekt státusz opciók sikeresen módosítva')
            })
            .catch(error => sendAlertMessage(error?.message || 'Projekt státuszok mentése sikertelen', 'error'))
    }

    // A projektlista a relevancia-szűrő szerint újraszámolva.
    const [filteredProjects, setFilteredProjects] = useState([]);
    useEffect(() => {
        switch (relevanceFilter) {
            case 'ALL':
                setFilteredProjects(projects);
                break;
            case 'RELEVANTS_ONLY':
                setFilteredProjects(projects.filter(p => p.relevance === 'RELEVANT'));
                break;
            case 'SEMI_RELEVANTS_ONLY':
                setFilteredProjects(projects.filter(p => p.relevance === 'SEMI_RELEVANT'));
                break;
            default:
                setFilteredProjects(projects);
                break;
        }
    }, [projects, relevanceFilter]);

    // Termék projektek selector adatai.
    const [productProjects, setProductProjects] = useState([]);
    useEffect(() => {
        productProjectService.getSelectors().then(setProductProjects).catch(console.error);
    }, []);

    // Projekt fejléc sablonok/selectorok.
    const [projectTasks, setProjectTasks] = useState([]);
    useEffect(() => {
        projectTaskService.getSelectors()
            .then(selectors => setProjectTasks(selectors.map(selector => ({description: selector}))))
            .catch(console.error);
    }, []);

    // Egy kiválasztott termék projekt sora (amount, stb.) módosítás.
    const onProductProjectChange = (e, index) => {
        const {name, value} = e.target
        setSelectedProductProjects(prev =>
            prev.map((item, i) => i === index ? {...item, [name]: value} : item)
        );
    };

    // Tervezett számla sor mezőinek módosítása.
    const onDraftInvoiceChange = (e, index) => {
        const {name, value} = e.target
        setDraftInvoices(prev =>
            prev.map((item, i) => i === index ? {...item, [name]: value} : item)
        );
    };

    // Részletek objektum egyszerű mezőváltozásai (text/select).
    const onProjectDetailsChange = (e) => {
        const {name, value} = e.target
        setProjectDetails(prev => ({
            ...prev, [name]: value
        }));
    };

    // Részletek checkbox mezők kezelése.
    const onProjectDetailsCheckboxChange = (event) => {
        const {checked, name} = event.target
        setProjectDetails((prev) => ({
            ...prev,
            [name]: checked,
        }));
    };

    // Beágyazott task módosító: selectedProjectTasks[projectTaskIndex].tasks[taskIndex][name].
    const onTaskChange = (projectTaskIndex, taskIndex, name, newValue) => {
        console.log(projectTaskIndex, taskIndex, name, newValue);
        setSelectedProjectTasks(prev =>
            prev.map((project, pIdx) => pIdx === projectTaskIndex ? {
                    ...project,
                    tasks: project?.tasks?.map((task, tIdx) => tIdx === taskIndex ? {...task, [name]: newValue} : task)
                } : project
            )
        );
    };


    // Újonnan kiválasztott helyi fájlok felvétele (duplikátum név+méret alapján szűrve).
    const handleFileChange = (event) => {
        const selectedFiles = Array.from(event.target.files);
        setSelectedProjectFiles((prevFiles) => {
            const newFiles = selectedFiles.filter(
                (file) => !prevFiles.some((f) => f.name === file.name && f.size === file.size)
            );
            return [...prevFiles, ...newFiles];
        });
        event.target.value = null;
    };

    // Feltöltésre kijelölt fájl törlése a lokális listából.
    const handleRemoveFile = (indexToRemove) => {
        setSelectedProjectFiles((prevFiles) => prevFiles.filter((_, index) => index !== indexToRemove));
    };

    // Termék projekt sorban kiválasztás cseréje (autocomplete).
    const handleProductProjectsChange = (index, newValue) => {
        setSelectedProductProjects((prev) => {
            const updated = [...prev];
            updated[index] = {
                id: newValue?.id,
                name: newValue?.name,
                amount: updated[index]?.amount
            };
            return updated;
        });
    };

    // Termék projekt sor hozzáadása.
    const handleAddProductProject = () => {
        setSelectedProductProjects((prev) => [...prev, null]);
    };

    // Termék projekt sor törlése.
    const handleRemoveProductProject = (index) => {
        setSelectedProductProjects((prev) => prev.filter((_, i) => i !== index));
    };

    // Fejléc sor kiválasztásának módosítása.
    const handleProjectTaskChange = (index, newValue) => {
        setSelectedProjectTasks((prev) => {
            const updated = [...prev];
            updated[index] = newValue
            return updated;
        });
    };

    // Fejléc sor hozzáadása.
    const handleAddProjectTask = () => {
        setSelectedProjectTasks((prev) => [...prev, null]);
    };

    // Fejléc sor törlése.
    const handleRemoveProjectTask = (index) => {
        setSelectedProjectTasks((prev) => prev.filter((_, i) => i !== index));
    };

    // Egy fejlécen belüli konkrét task törlése.
    const handleRemoveTaskOfProjectTask = (index, taskIndex) => {
        setSelectedProjectTasks((prev) =>
            prev.map((projectTask, i) => {
                if (i !== index) return projectTask;
                return {
                    ...projectTask,
                    tasks: projectTask.tasks.filter((_, j) => j !== taskIndex)
                };
            })
        );
    };

    // Új task beszúrása egy fejléc alá.
    const handleAddTaskToProjectTask = (index, newTask) => {
        setSelectedProjectTasks((prev) =>
            prev.map((projectTask, i) => {
                if (i !== index) return projectTask;
                return {
                    ...projectTask,
                    tasks: [...(projectTask?.tasks || []), newTask]
                };
            })
        );
    };


    // Tervezett számla sor hozzáadása.
    const handleAddDraftInvoice = () => {
        setDraftInvoices((prev) => [...prev, null]);
    };

    // Tervezett számla sor törlése.
    const handleRemoveDraftInvoice = (index) => {
        setDraftInvoices((prev) => prev.filter((_, i) => i !== index));
    };

    // Projektfájlok feltöltése backendbe, majd UI frissítés.
    const uploadProjectFiles = () => {
        if (project?.id == null) return
        projectService.uploadProjectFiles(project.id, selectedProjectFiles)
            .then(() => {
                setSelectedProjectFiles([])
                retrieveProjectStructure()
                sendAlertMessage("Projekt mellékletek feltöltése sikeres")
            })
            .catch(error => sendAlertMessage(error?.message || "Projekt mellékletek feltöltése sikertelen", 'error'));
    }

    // Termék projekt blokk mentése.
    const updateProductProjects = () => {
        if (project?.id == null) return
        projectService.updateProductProjects(project.id, {productProjects: selectedProductProjects, productProjectsDescription: projectDetails?.productProjectsDescription})
            .then(() => {
                retrieveProjectStructure()
                sendAlertMessage("Termék projektek mentve")
            })
            .catch(error => sendAlertMessage(error?.message || "Termék projektek mentése sikertelen", 'error'));
    }

    // Részletek blokk mentése.
    const updateProjectDetails = () => {
        if (project?.id == null) return
        projectService.updateProjectDetails(project.id, projectDetails)
            .then(() => {
                retrieveProjectStructure()
                sendAlertMessage("Adatok mentve")
            })
            .catch(error => sendAlertMessage(error?.message || "Adatok mentése sikertelen", 'error'));
    }

    // Fejlécek + feladatok blokk mentése.
    const updateProjectTasks = () => {
        if (project?.id == null) return
        projectService.updateProjectTasks(project.id, {projectTasks: selectedProjectTasks, projectTasksDescription: projectDetails?.projectTasksDescription})
            .then(() => {
                retrieveProjectStructure()
                sendAlertMessage("Fejlécek mentve")
            })
            .catch(error => sendAlertMessage(error?.message || "Fejlécek mentése sikertelen", 'error'));
    }

    // Számla-tervek blokk mentése.
    const updateDraftInvoices = () => {
        if (project?.id == null) return
        projectService.updateDraftInvoices(project.id,
            {
                draftInvoices: draftInvoices,
                draftInvoicesDescription: projectDetails?.draftInvoicesDescription,
                otherPlannedExpenses: projectDetails?.otherPlannedExpenses
            }
        )
            .then(() => {
                retrieveProjectStructure()
                sendAlertMessage("Számla tervek mentve")
            })
            .catch(error => sendAlertMessage(error?.message || "Számla tervek mentése sikertelen", 'error'));
    }

    // Backendben már tárolt projektfájl törlése.
    const deleteProjectFile = (fileId) => {
        if (project?.id == null) return
        projectService.deleteProjectFile(project.id, fileId)
            .then(() => {
                retrieveProjectStructure()
                sendAlertMessage('Fájl sikeresen törölve!')
            })
            .catch(error => sendAlertMessage(error?.message || "Fájl törlése sikertelen", 'error'))
    };

    // Relevancia állapot ciklikus váltása backend oldalon.
    const toggleRelevance = (id) => {
        projectService.toggleRelevance(id)
            .then(response => {
                setProjectDetails(prev => ({...prev, relevance: response}))
            })
            .catch(error => sendAlertMessage(error?.message || "Művelet sikertelen", 'error'))
    }

    // Lista-szűrő ciklizálása: ALL -> RELEVANTS_ONLY -> SEMI_RELEVANTS_ONLY -> ALL.
    const toggleRelevanceFilter = () => {
        switch (relevanceFilter) {
            case 'ALL':
                setRelevanceFilter('RELEVANTS_ONLY');
                break;
            case 'RELEVANTS_ONLY':
                setRelevanceFilter('SEMI_RELEVANTS_ONLY');
                break;
            case 'SEMI_RELEVANTS_ONLY':
                setRelevanceFilter('ALL');
                break;
            default:
                setRelevanceFilter('ALL');
                break;
        }
    }

    // Lista elem tipográfia egységesítés MUI ListItemText-hez.
    const listItemTextProps = {
        primary: {
            noWrap: true,
            sx: {fontSize: "0.9rem"},
        },
        secondary: {
            noWrap: true,
            sx: {fontSize: "0.75rem", color: "text.secondary"},
        }
    }

    // Kiválasztott projekthez képest előző/következő projektre ugrás.
    const onNextProjectButtonClick = (direction) => {
        if (!project) return; // nothing selected yet

        const index = filteredProjects.findIndex(p => p.id === project.id);
        if (index + direction >= 0 && index + direction < filteredProjects.length) {
            const prevProject = filteredProjects[index + direction];
            setProject(prevProject);
            localStorage.setItem(LOCAL_STORAGE_NAME, JSON.stringify(prevProject));
        }
    };

    // Task dolgozólista többválasztós kezelő (toggle jellegű kiválasztással).
    const onUsersChange = (projectTaskIndex, taskIndex, event) => {
        const {value} = event.target;
        const newSelection = Array.isArray(value) ? value : value.split(',');

        // toggle logic (add if not exists, remove if exists)
        const uniqueSelection = newSelection.reduce((acc, user) => {
            const exists = acc.find(item => item.id === user.id);
            return exists
                ? acc.filter(item => item.id !== user.id)
                : [...acc, user];
        }, []);

        setSelectedProjectTasks(prev =>
            prev.map((project, pIdx) =>
                pIdx === projectTaskIndex
                    ? {
                        ...project,
                        tasks: project.tasks?.map((task, tIdx) =>
                            tIdx === taskIndex
                                ? {...task, users: uniqueSelection}
                                : task
                        )
                    }
                    : project
            )
        );
    };


    // Többválasztós menü beállítások.
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: 48 * 4.5 + 8,
                width: 250
            },
        },
    };

    // Select menüben vizuális kiemelés, ha az adott user már kiválasztott.
    function getStyles(name, users) {
        if (users == null || users.map(user => user.name).indexOf(name) === -1) return
        return {
            backgroundColor: "#2e7d32",
            color: "white"
        };
    }

    // Mentés + navigáció kombináció új termék projekt létrehozásához.
    const saveAndNavigateToProductProjects = () => {
        projectService.updateProductProjects(project.id, {productProjects: selectedProductProjects, productProjectsDescription: projectDetails?.productProjectsDescription})
            .then(() => {
                navigate("/dashboard/product-projects/create");
            })
            .catch(error => sendAlertMessage(error?.message || "Termék projektek mentése sikertelen", 'error'));
    }

    // Óra-progresszió grafikon adattranszformáció (objektum -> időrendbe rendezett pontok).
    const timeGraphData = Object.entries(projectDetails?.hoursProgress ?? {})
        .map(([date, value]) => ({
            x: new Date(date),
            y: value,
        }))
        .sort((a, b) => a.x.getTime() - b.x.getTime());

    // Pénzügyi progresszió grafikon adattranszformáció.
    const financialGraphData = Object.entries(projectDetails?.financialProgress ?? {})
        .map(([date, value]) => ({
            x: new Date(date),
            y: value,
        }))
        .sort((a, b) => a.x.getTime() - b.x.getTime());

    const estimatedTotal = projectDetails?.totalEstimatedHours ?? 0;
    const estimatedAmount = draftInvoices?.reduce((total, i) => total + (i?.amount || 0), 0) || 0;
    const monthlyDataset = Object.entries(monthlyCosts).map(([key, value]) => ({
        month: key.slice(0, 7),
        totalPaidExpense: value?.[0] || 0,
        totalUnpaidExpense: value?.[1] || 0,
        totalPaidIncome: value?.[2] || 0,
        totalUnpaidIncome: value?.[3] || 0
    }));

    const SERIES_LIBRARY = useMemo(() => ([
        {key: "actualHours", label: "Teljesített Órák", color: "#00a8cc", lineWidth: 2, lineStyle: "solid"},
        {key: "estimatedHours", label: "Becsült Órák", color: "#ff4d6d", lineWidth: 2, lineStyle: "dashed"},
        {key: "paidAmount", label: "Kifizetett Számlák", color: "#3a86ff", lineWidth: 2, lineStyle: "solid"},
        {key: "unpaidInvoices", label: "Kifizetetlen Számlák", color: "#8338ec", lineWidth: 2, lineStyle: "solid"},
        {key: "estimatedAmount", label: "Becsült Összeg", color: "#ff9f1c", lineWidth: 2, lineStyle: "dashed"},
        {key: "paidExpense", label: "Teljesített Kiadás", color: "#ef476f", lineWidth: 1, lineStyle: "solid", stack: "expense"},
        {key: "unpaidExpense", label: "Teljesítetlen Kiadás", color: "#ff99c8", lineWidth: 1, lineStyle: "solid", stack: "expense"},
        {key: "paidIncome", label: "Teljesített Bevétel", color: "#06d6a0", lineWidth: 1, lineStyle: "solid", stack: "income"},
        {key: "unpaidIncome", label: "Teljesítetlen Bevétel", color: "#90e0ef", lineWidth: 1, lineStyle: "solid", stack: "income"},
    ]), []);
    const ALL_SERIES_KEYS = useMemo(() => SERIES_LIBRARY.map((s) => s.key), [SERIES_LIBRARY]);

    const buildSeriesConfig = (enabledKeys) => SERIES_LIBRARY.map(s => ({...s, enabled: enabledKeys.includes(s.key)}));

    const DEFAULT_STYLE = useMemo(() => ({
        cardBackground: "#fffdf8",
        plotBackground: "#f8fbff",
        borderColor: "#c9daf6",
        gridColor: "#e9eff8",
        textColor: "#465875",
    }), []);

    const CHART_TEMPLATES = useMemo(() => ({
        hoursLine: {
            title: "Óra trend",
            type: "line",
            keys: ["actualHours", "estimatedHours"],
            style: {
                cardBackground: "#fffef7",
                plotBackground: "#f7fffb",
                borderColor: "#c8e8da",
                gridColor: "#e5f3ec",
                textColor: "#37574a",
            }
        },
        financialLine: {
            title: "Pénzügyi trend",
            type: "line",
            keys: ["paidAmount", "unpaidInvoices", "estimatedAmount"],
            style: {
                cardBackground: "#f8fbff",
                plotBackground: "#f6f8ff",
                borderColor: "#c9d8ff",
                gridColor: "#e6ecff",
                textColor: "#3d4f79",
            }
        },
        monthlyBar: {
            title: "Havi bevétel/kiadás",
            type: "bar",
            keys: ["paidExpense", "unpaidExpense", "paidIncome", "unpaidIncome"],
            style: {
                cardBackground: "#fff9ff",
                plotBackground: "#fbf7ff",
                borderColor: "#e0cef8",
                gridColor: "#f0e7fb",
                textColor: "#5a4f77",
            }
        }
    }), []);

    const CHART_THEMES = useMemo(() => ({
        ocean: {
            label: "Ocean",
            style: {
                cardBackground: "#f5fcff",
                plotBackground: "#f3f9ff",
                borderColor: "#bfe2f5",
                gridColor: "#dff0fa",
                textColor: "#2f5873",
            },
            seriesColors: {
                actualHours: "#00b4d8",
                estimatedHours: "#48cae4",
                paidAmount: "#0077b6",
                unpaidInvoices: "#3a86ff",
                estimatedAmount: "#90e0ef",
                paidExpense: "#ff6b6b",
                unpaidExpense: "#ffadad",
                paidIncome: "#06d6a0",
                unpaidIncome: "#80ed99",
            }
        },
        sunset: {
            label: "Sunset",
            style: {
                cardBackground: "#fff7f2",
                plotBackground: "#fff9f5",
                borderColor: "#ffd7c2",
                gridColor: "#ffe8da",
                textColor: "#6f4b3e",
            },
            seriesColors: {
                actualHours: "#ff7b00",
                estimatedHours: "#ff9e00",
                paidAmount: "#ff006e",
                unpaidInvoices: "#8338ec",
                estimatedAmount: "#fb5607",
                paidExpense: "#d62828",
                unpaidExpense: "#ffb4a2",
                paidIncome: "#2a9d8f",
                unpaidIncome: "#52b788",
            }
        },
        candy: {
            label: "Candy",
            style: {
                cardBackground: "#fff7ff",
                plotBackground: "#fcf6ff",
                borderColor: "#e9d1fb",
                gridColor: "#f2e5ff",
                textColor: "#5c4b76",
            },
            seriesColors: {
                actualHours: "#5e60ce",
                estimatedHours: "#64dfdf",
                paidAmount: "#ff4d8d",
                unpaidInvoices: "#9d4edd",
                estimatedAmount: "#ffb703",
                paidExpense: "#ef476f",
                unpaidExpense: "#ffa3c4",
                paidIncome: "#06d6a0",
                unpaidIncome: "#90e0ef",
            }
        }
    }), []);

    const getRenderOptionsForTemplate = (templateKey) => {
        const templateType = CHART_TEMPLATES?.[templateKey]?.type;
        if (templateType === "bar") return ["bar", "pie"];
        return ["line", "bar", "pie"];
    };

    const [templateDefaults, setTemplateDefaults] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem(CHART_TEMPLATE_DEFAULTS)) || {};
        } catch {
            return {};
        }
    });

    useEffect(() => {
        localStorage.setItem(CHART_TEMPLATE_DEFAULTS, JSON.stringify(templateDefaults));
    }, [templateDefaults]);

    const normalizeHexColor = (value, fallback = "#000000") => {
        if (typeof value !== "string") return fallback;
        const trimmed = value.trim();
        if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed;
        if (/^[0-9a-fA-F]{6}$/.test(trimmed)) return `#${trimmed}`;
        if (/^#[0-9a-fA-F]{3}$/.test(trimmed)) {
            const c = trimmed.slice(1);
            return `#${c[0]}${c[0]}${c[1]}${c[1]}${c[2]}${c[2]}`;
        }
        if (/^[0-9a-fA-F]{3}$/.test(trimmed)) {
            const c = trimmed;
            return `#${c[0]}${c[0]}${c[1]}${c[1]}${c[2]}${c[2]}`;
        }
        return fallback;
    };

    const getContrastTextColor = (bgColor) => {
        const hex = normalizeHexColor(bgColor, "#ffffff").slice(1);
        const r = parseInt(hex.slice(0, 2), 16) / 255;
        const g = parseInt(hex.slice(2, 4), 16) / 255;
        const b = parseInt(hex.slice(4, 6), 16) / 255;
        const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
        return luminance > 0.62 ? "#111111" : "#ffffff";
    };

    const getTemplateDefaultSetup = (templateKey) => {
        const template = CHART_TEMPLATES[templateKey] || {};
        const saved = templateDefaults?.[templateKey] || {};
        const enabledKeys = Array.isArray(saved.enabledKeys) && saved.enabledKeys.length > 0
            ? saved.enabledKeys
            : (template.keys || []);
        const visibleKeys = Array.isArray(saved.visibleKeys) && saved.visibleKeys.length > 0
            ? saved.visibleKeys
            : ALL_SERIES_KEYS;
        const availableRenderTypes = getRenderOptionsForTemplate(templateKey);
        const renderType = availableRenderTypes.includes(saved?.renderType) ? saved.renderType : availableRenderTypes[0];
        return {
            enabledKeys,
            visibleKeys,
            renderType,
            range: {
                from: saved?.range?.from || "",
                to: saved?.range?.to || ""
            },
            style: {
                ...DEFAULT_STYLE,
                ...(template.style || {}),
                ...(saved.style || {})
            }
        };
    };

    const createChartCard = (templateKey) => {
        const template = CHART_TEMPLATES[templateKey];
        const setup = getTemplateDefaultSetup(templateKey);
        const initialSeriesConfig = buildSeriesConfig(setup.enabledKeys);
        return {
            id: `${templateKey}-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
            templateKey,
            title: template.title,
            type: template.type,
            renderType: setup.renderType,
            seriesConfig: initialSeriesConfig,
            visibleKeys: [...setup.visibleKeys],
            variableFilter: "",
            seriesOpen: {},
            range: {...setup.range},
            style: {...setup.style},
            open: true,
            openSetup: false
        };
    };

    const [newChartTemplate, setNewChartTemplate] = useState("hoursLine");
    const [chartCards, setChartCards] = useState(() => ([
        createChartCard("hoursLine"),
        createChartCard("financialLine"),
        createChartCard("monthlyBar")
    ]));

    const lineStyleOptions = ["solid", "dashed", "dotted"];

    const isInRange = (date, range) => {
        const timestamp = date instanceof Date ? date.getTime() : new Date(date).getTime();
        if (Number.isNaN(timestamp)) return false;
        const from = range?.from ? new Date(range.from + "T00:00:00").getTime() : null;
        const to = range?.to ? new Date(range.to + "T23:59:59.999").getTime() : null;
        if (from != null && timestamp < from) return false;
        if (to != null && timestamp > to) return false;
        return true;
    };

    const buildMonthlyLatest = (data) => {
        const map = new Map();
        data.forEach((d) => {
            const key = d.x.toISOString().slice(0, 7);
            map.set(key, d.y);
        });
        return map;
    };

    const getCardData = (range) => {
        const filteredTimeGraphData = timeGraphData.filter(d => isInRange(d.x, range));
        const filteredFinancialGraphData = financialGraphData.filter(d => isInRange(d.x, range));
        const filteredMonthlyDataset = monthlyDataset.filter(d => isInRange(new Date(d.month + "-01"), range));

        const hoursByTime = new Map(filteredTimeGraphData.map(d => [d.x.getTime(), d.y]));
        const financialByTime = new Map(filteredFinancialGraphData.map(d => [d.x.getTime(), d.y]));
        const monthlyByMonth = new Map(filteredMonthlyDataset.map(d => [d.month, d]));

        const hoursMonthlyLatest = buildMonthlyLatest(filteredTimeGraphData);
        const financialMonthlyLatest = buildMonthlyLatest(filteredFinancialGraphData);

        const unifiedTimeline = Array.from(new Set([
            ...filteredTimeGraphData.map(d => d.x.getTime()),
            ...filteredFinancialGraphData.map(d => d.x.getTime())
        ])).sort((a, b) => a - b);

        const monthlyLabels = filteredMonthlyDataset.length > 0
            ? filteredMonthlyDataset.map(d => d.month)
            : Array.from(new Set([...hoursMonthlyLatest.keys(), ...financialMonthlyLatest.keys()])).sort();

        return {
            filteredTimeGraphData,
            filteredFinancialGraphData,
            filteredMonthlyDataset,
            hoursByTime,
            financialByTime,
            monthlyByMonth,
            hoursMonthlyLatest,
            financialMonthlyLatest,
            unifiedTimeline,
            monthlyLabels
        };
    };

    const formatSummaryNumber = (value) => {
        if (value == null || Number.isNaN(value)) return "-";
        return Number(value).toLocaleString("hu-HU");
    };

    const getAggregateForKey = (key, cardData) => {
        if (key === "actualHours") return cardData.filteredTimeGraphData.length > 0 ? (Number(cardData.filteredTimeGraphData[cardData.filteredTimeGraphData.length - 1].y) || 0) : 0;
        if (key === "estimatedHours") return estimatedTotal;
        if (key === "paidAmount") return cardData.filteredFinancialGraphData.length > 0 ? (Number(cardData.filteredFinancialGraphData[cardData.filteredFinancialGraphData.length - 1].y) || 0) : 0;
        if (key === "unpaidInvoices") return cardData.filteredMonthlyDataset.reduce((sum, d) => sum + (Number(d.totalUnpaidIncome) || 0), 0);
        if (key === "estimatedAmount") return estimatedAmount;
        if (key === "paidExpense") return cardData.filteredMonthlyDataset.reduce((sum, d) => sum + (Number(d.totalPaidExpense) || 0), 0);
        if (key === "unpaidExpense") return cardData.filteredMonthlyDataset.reduce((sum, d) => sum + (Number(d.totalUnpaidExpense) || 0), 0);
        if (key === "paidIncome") return cardData.filteredMonthlyDataset.reduce((sum, d) => sum + (Number(d.totalPaidIncome) || 0), 0);
        if (key === "unpaidIncome") return cardData.filteredMonthlyDataset.reduce((sum, d) => sum + (Number(d.totalUnpaidIncome) || 0), 0);
        return 0;
    };

    const renderSetupSummary = (series, cardData) => {
        const enabled = series.filter(s => s.enabled);
        if (enabled.length === 0) {
            return <Typography sx={{fontSize: "0.74rem", color: "#77889b"}}>Nincs kiválasztott változó</Typography>;
        }
        return (
            <Typography sx={{
                fontSize: "0.74rem",
                color: "#4f647c",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "calc(100% - 36px)"
            }}>
                {enabled.map(item => `${item.label}: ${formatSummaryNumber(getAggregateForKey(item.key, cardData))}`).join("  |  ")}
            </Typography>
        );
    };

    const updateCard = (cardId, updater) => {
        setChartCards(prev => prev.map(c => c.id === cardId ? updater(c) : c));
    };

    const resetCardToDefaults = (cardId) => {
        updateCard(cardId, (card) => {
            const setup = getTemplateDefaultSetup(card.templateKey);
            return {
                ...card,
                renderType: setup.renderType,
                seriesConfig: buildSeriesConfig(setup.enabledKeys),
                visibleKeys: [...setup.visibleKeys],
                variableFilter: "",
                seriesOpen: {},
                style: {...setup.style},
                range: {...setup.range}
            };
        });
    };

    const saveCardAsTemplateDefaults = (cardId) => {
        const card = chartCards.find(c => c.id === cardId);
        if (!card?.templateKey) return;
        const enabledKeys = card.seriesConfig.filter(s => s.enabled).map(s => s.key);
        setTemplateDefaults(prev => ({
            ...prev,
            [card.templateKey]: {
                enabledKeys,
                visibleKeys: [...(card.visibleKeys || ALL_SERIES_KEYS)],
                renderType: card.renderType,
                range: {...card.range},
                style: {...card.style}
            }
        }));
    };

    const applyThemeToCard = (cardId, themeKey) => {
        const theme = CHART_THEMES[themeKey];
        if (!theme) return;
        updateCard(cardId, (card) => ({
            ...card,
            style: {
                ...card.style,
                ...(theme.style || {})
            },
            seriesConfig: card.seriesConfig.map((s) => ({
                ...s,
                color: theme?.seriesColors?.[s.key] || s.color
            }))
        }));
    };

    const removeCard = (cardId) => {
        setChartCards(prev => prev.filter(c => c.id !== cardId));
    };

    const getLineSeriesForCard = (card, cardData) => card.seriesConfig
        .filter(s => s.enabled)
        .map((s) => {
            const resolveLineValue = (timestamp) => {
                const monthKey = new Date(timestamp).toISOString().slice(0, 7);
                const monthData = cardData.monthlyByMonth.get(monthKey);
                if (s.key === "actualHours") return cardData.hoursByTime.get(timestamp) ?? null;
                if (s.key === "estimatedHours") return estimatedTotal;
                if (s.key === "paidAmount") return cardData.financialByTime.get(timestamp) ?? null;
                if (s.key === "unpaidInvoices") return monthData?.totalUnpaidIncome ?? null;
                if (s.key === "estimatedAmount") return estimatedAmount;
                if (s.key === "paidExpense") return monthData?.totalPaidExpense ?? null;
                if (s.key === "unpaidExpense") return monthData?.totalUnpaidExpense ?? null;
                if (s.key === "paidIncome") return monthData?.totalPaidIncome ?? null;
                return monthData?.totalUnpaidIncome ?? null;
            };
            return {
                label: s.label,
                color: s.color,
                lineWidth: Number(s.lineWidth) || 2,
                lineStyle: s.lineStyle,
                points: cardData.unifiedTimeline.map(timestamp => ({x: timestamp, y: resolveLineValue(timestamp)}))
            };
        });

    const getBarSeriesForCard = (card, cardData) => card.seriesConfig
        .filter(s => s.enabled)
        .map((s) => {
            const resolveBarValue = (month) => {
                const monthData = cardData.monthlyByMonth.get(month);
                if (s.key === "actualHours") return cardData.hoursMonthlyLatest.get(month) ?? null;
                if (s.key === "estimatedHours") return estimatedTotal;
                if (s.key === "paidAmount") return cardData.financialMonthlyLatest.get(month) ?? null;
                if (s.key === "unpaidInvoices") return monthData?.totalUnpaidIncome ?? null;
                if (s.key === "estimatedAmount") return estimatedAmount;
                if (s.key === "paidExpense") return monthData?.totalPaidExpense ?? null;
                if (s.key === "unpaidExpense") return monthData?.totalUnpaidExpense ?? null;
                if (s.key === "paidIncome") return monthData?.totalPaidIncome ?? null;
                return monthData?.totalUnpaidIncome ?? null;
            };
            return {
                label: s.label,
                color: s.color,
                lineWidth: Number(s.lineWidth) || 1,
                lineStyle: s.lineStyle,
                stack: s.stack,
                data: cardData.monthlyLabels.map(month => resolveBarValue(month))
            };
        });

    const getPieSeriesForCard = (card, cardData) => card.seriesConfig
        .filter(s => s.enabled)
        .map((s) => ({
            label: s.label,
            color: s.color,
            value: Math.max(0, Number(getAggregateForKey(s.key, cardData)) || 0)
        }))
        .filter((s) => s.value > 0);

    const addChartCard = () => setChartCards(prev => [...prev, createChartCard(newChartTemplate)]);

    const chartRangeSignature = useMemo(
        () => chartCards.map(card => `${card.id}:${card.range?.from || ""}:${card.range?.to || ""}`).join("|"),
        [chartCards]
    );

    const cardDataById = useMemo(() => {
        const result = {};
        chartCards.forEach((card) => {
            result[card.id] = getCardData(card.range);
        });
        return result;
    }, [chartRangeSignature, timeGraphData, financialGraphData, monthlyDataset]);

    const renderSetupPanel = (card) => (
        <Box sx={{
            border: "1px solid rgba(25, 118, 210, 0.12)",
            borderRadius: "10px",
            padding: "8px",
            marginBottom: "8px",
            backgroundColor: "#fcfdff",
        }}>
            <Typography sx={{fontWeight: 700, fontSize: "0.8rem", color: "#203248", marginBottom: "6px"}}>Setup</Typography>
            <Grid container spacing={0.8} columns={24}>
                <Grid size={{xs: 24}}>
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.8, alignItems: "center"}}>
                        <FormControl size="small" sx={{width: 276, maxWidth: "100%"}}>
                        <InputLabel id={`render-type-label-${card.id}`}>Grafikon típus</InputLabel>
                        <Select
                            labelId={`render-type-label-${card.id}`}
                            value={card.renderType || "line"}
                            label="Grafikon típus"
                            onChange={(e) => updateCard(card.id, c => ({...c, renderType: e.target.value}))}
                        >
                            {getRenderOptionsForTemplate(card.templateKey).map((view) => (
                                <MenuItem key={view} value={view}>
                                    {view === "line" ? "Folyamatos (line)" : view === "bar" ? "Oszlop (bar)" : "Kördiagram (pie)"}
                                </MenuItem>
                            ))}
                        </Select>
                        </FormControl>
                        <TextField
                            size="small"
                            type="date"
                            label="Dátumtól"
                            slotProps={{inputLabel: {shrink: true}}}
                            value={card.range.from}
                            onChange={(e) => updateCard(card.id, c => ({...c, range: {...c.range, from: e.target.value}}))}
                            sx={{width: 276, maxWidth: "100%"}}
                        />
                        <TextField
                            size="small"
                            type="date"
                            label="Dátumig"
                            slotProps={{inputLabel: {shrink: true}}}
                            value={card.range.to}
                            onChange={(e) => updateCard(card.id, c => ({...c, range: {...c.range, to: e.target.value}}))}
                            sx={{width: 276, maxWidth: "100%"}}
                        />
                        <FormControl size="small" sx={{width: 276, maxWidth: "100%"}}>
                        <InputLabel id={`visible-series-label-${card.id}`}>Elérhető változók</InputLabel>
                        <Select
                            labelId={`visible-series-label-${card.id}`}
                            multiple
                            value={card.visibleKeys || ALL_SERIES_KEYS}
                            onClose={() => updateCard(card.id, c => ({...c, variableFilter: ""}))}
                            onChange={(e) => {
                                const nextVisibleKeys = e.target.value;
                                updateCard(card.id, c => ({
                                    ...c,
                                    visibleKeys: nextVisibleKeys,
                                    seriesConfig: c.seriesConfig.map(s => ({
                                        ...s,
                                        enabled: nextVisibleKeys.includes(s.key) ? s.enabled : false
                                    }))
                                }));
                            }}
                            input={<OutlinedInput label="Elérhető változók"/>}
                            renderValue={(selected) => `${selected.length} változó`}
                        >
                            <ListSubheader sx={{backgroundColor: "#fff", py: 0.7}}>
                                <TextField
                                    size="small"
                                    autoFocus
                                    fullWidth
                                    placeholder="Változó szűrő..."
                                    value={card.variableFilter || ""}
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => e.stopPropagation()}
                                    onChange={(e) => updateCard(card.id, c => ({...c, variableFilter: e.target.value}))}
                                    sx={{"& .MuiInputBase-input": {fontSize: "0.72rem", py: 0.45}}}
                                />
                            </ListSubheader>
                            {SERIES_LIBRARY
                                .filter((s) => {
                                    const q = (card.variableFilter || "").trim().toLowerCase();
                                    if (!q) return true;
                                    return s.label.toLowerCase().includes(q) || s.key.toLowerCase().includes(q);
                                })
                                .map((s) => (
                                <MenuItem key={s.key} value={s.key}>
                                    <Checkbox size="small" checked={(card.visibleKeys || ALL_SERIES_KEYS).includes(s.key)}/>
                                    <Typography sx={{fontSize: "0.74rem"}}>{s.label}</Typography>
                                </MenuItem>
                            ))}
                            {SERIES_LIBRARY.filter((s) => {
                                const q = (card.variableFilter || "").trim().toLowerCase();
                                if (!q) return true;
                                return s.label.toLowerCase().includes(q) || s.key.toLowerCase().includes(q);
                            }).length === 0 && (
                                <MenuItem disabled sx={{fontSize: "0.72rem"}}>
                                    Nincs találat
                                </MenuItem>
                            )}
                        </Select>
                        </FormControl>
                    </Box>
                </Grid>
                <Grid size={{xs: 24}} sx={{display: "flex", gap: 0.8, alignItems: "center", flexWrap: "wrap", mt: 0.5, mb: 0.4}}>
                    <Typography sx={{fontSize: "0.72rem", color: "#4f647c"}}>Háttér:</Typography>
                    {[
                        {key: "cardBackground", label: "Kártya"},
                        {key: "plotBackground", label: "Plot"},
                        {key: "borderColor", label: "Keret"},
                        {key: "gridColor", label: "Rács"},
                        {key: "textColor", label: "Szöveg"},
                    ].map((field) => (
                        <Box key={field.key} sx={{display: "flex", alignItems: "center", gap: "5px"}}>
                            <Typography sx={{fontSize: 10.5, color: "#4f647c"}}>{field.label}</Typography>
                            <input
                                type="color"
                                value={card.style[field.key]}
                                onChange={(e) => {
                                    const color = normalizeHexColor(e.target.value, card.style[field.key]);
                                    updateCard(card.id, c => ({...c, style: {...c.style, [field.key]: color}}));
                                }}
                                style={{width: 32, height: 22, border: "1px solid #cdd7e3", borderRadius: 4, background: "transparent", cursor: "pointer"}}
                            />
                            <TextField
                                size="small"
                                value={card.style[field.key]}
                                onChange={(e) => {
                                    const next = normalizeHexColor(e.target.value, card.style[field.key]);
                                    updateCard(card.id, c => ({...c, style: {...c.style, [field.key]: next}}));
                                }}
                                onBlur={(e) => {
                                    const normalized = normalizeHexColor(e.target.value, card.style[field.key]);
                                    updateCard(card.id, c => ({...c, style: {...c.style, [field.key]: normalized}}));
                                }}
                                slotProps={{htmlInput: {maxLength: 7}}}
                                sx={{width: 72, "& .MuiInputBase-input": {fontSize: "0.7rem", py: 0.45, px: 0.7}}}
                            />
                        </Box>
                    ))}
                    <Typography sx={{fontSize: "0.72rem", color: "#4f647c", ml: 0.5}}>Téma:</Typography>
                    {Object.entries(CHART_THEMES).map(([themeKey, theme]) => (
                        <Button
                            key={themeKey}
                            size="small"
                            variant="contained"
                            onClick={() => applyThemeToCard(card.id, themeKey)}
                            sx={{
                                textTransform: "none",
                                fontSize: "0.7rem",
                                minWidth: "62px",
                                py: 0.3,
                                boxShadow: "none",
                                backgroundColor: theme.style.cardBackground,
                                color: theme.style.textColor,
                                border: `1px solid ${theme.style.borderColor}`,
                                "&:hover": {backgroundColor: theme.style.plotBackground, boxShadow: "none"}
                            }}
                        >
                            {theme.label}
                        </Button>
                    ))}
                </Grid>
                <Grid size={{xs: 24}} sx={{display: "flex", gap: 0.8, alignItems: "center", flexWrap: "wrap", mt: 0.5}}>
                    <Button variant="outlined" size="small" sx={{textTransform: "none", fontSize: "0.72rem", py: 0.4}} onClick={() => updateCard(card.id, c => ({...c, range: {from: "", to: ""}}))}>
                        Intervallum törlése
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        size="small"
                        sx={{textTransform: "none", fontSize: "0.72rem", py: 0.4, boxShadow: "none"}}
                        onClick={() => resetCardToDefaults(card.id)}
                    >
                        Alapértékek betöltése
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        sx={{textTransform: "none", fontSize: "0.7rem", py: 0.3}}
                        onClick={() => saveCardAsTemplateDefaults(card.id)}
                    >
                        Mentés defaultnak
                    </Button>
                </Grid>

                <Grid size={{xs: 24}}>
                    <Box sx={{maxHeight: "33vh", overflowY: "auto", pr: 0.5}}>
                        <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.8, alignItems: "flex-start"}}>
                            {card.seriesConfig
                                .filter((item) => (card.visibleKeys || ALL_SERIES_KEYS).includes(item.key))
                                .map((item) => {
                                    const summaryTextColor = item.enabled ? getContrastTextColor(item.color) : "#1f2b3a";
                                    const accordionBg = item.enabled ? normalizeHexColor(item.color, "#ffffff") : "#ffffff";
                                    return (
                                    <Box key={item.key} sx={{width: "276px", flex: "0 0 276px", maxWidth: "100%"}}>
                                        <Accordion
                                            disableGutters
                                            expanded={!!card.seriesOpen?.[item.key]}
                                            onChange={() => updateCard(card.id, c => ({
                                                ...c,
                                                seriesOpen: {...(c.seriesOpen || {}), [item.key]: !(c.seriesOpen?.[item.key])}
                                            }))}
                                            sx={{
                                                border: "1px solid rgba(0,0,0,0.07)",
                                                borderRadius: "7px !important",
                                                boxShadow: "none",
                                                "&:before": {display: "none"},
                                                backgroundColor: accordionBg,
                                            }}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMore/>}
                                                sx={{
                                                    minHeight: "30px !important",
                                                    px: 1,
                                                    color: summaryTextColor,
                                                    "& .MuiAccordionSummary-content": {my: 0.4},
                                                    "& .MuiAccordionSummary-expandIconWrapper": {color: summaryTextColor}
                                                }}
                                            >
                                                <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 0.8}}>
                                                    <Box sx={{display: "flex", alignItems: "center", minWidth: 0}}>
                                                        <Checkbox
                                                            size="small"
                                                            checked={item.enabled}
                                                            onClick={(e) => e.stopPropagation()}
                                                            onFocus={(e) => e.stopPropagation()}
                                                            onChange={(e) => updateCard(card.id, c => ({
                                                                ...c,
                                                                seriesConfig: c.seriesConfig.map(s => s.key === item.key ? {...s, enabled: e.target.checked} : s)
                                                            }))}
                                                            sx={{
                                                                p: 0.3,
                                                                mr: 0.5,
                                                                color: summaryTextColor,
                                                                "&.Mui-checked": {color: summaryTextColor}
                                                            }}
                                                        />
                                                        <Typography sx={{fontSize: "0.72rem", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: summaryTextColor}}>
                                                            {item.label}
                                                        </Typography>
                                                    </Box>
                                                    <Typography sx={{fontSize: "0.66rem", color: summaryTextColor, pr: 0.5, whiteSpace: "nowrap", opacity: 0.95}}>
                                                        {item.lineStyle} | {item.lineWidth}px
                                                    </Typography>
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{pt: 0.3, pb: 0.8, px: 1}}>
                                                <Box sx={{display: "grid", gridTemplateColumns: "36px 72px minmax(130px, 1fr)", gap: 0.8, alignItems: "center"}}>
                                                    <input
                                                        type="color"
                                                        value={item.color}
                                                        onChange={(e) => updateCard(card.id, c => ({
                                                            ...c,
                                                            seriesConfig: c.seriesConfig.map(s => s.key === item.key ? {...s, color: e.target.value} : s)
                                                        }))}
                                                        style={{width: "100%", height: "22px", border: "1px solid #d5dbe3", borderRadius: "4px", background: "#fff"}}
                                                    />
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        value={item.lineWidth}
                                                        onChange={(e) => updateCard(card.id, c => ({
                                                            ...c,
                                                            seriesConfig: c.seriesConfig.map(s => s.key === item.key ? ({
                                                                ...s,
                                                                lineWidth: Math.max(1, Number(e.target.value || 1))
                                                            }) : s)
                                                        }))}
                                                        slotProps={{htmlInput: {min: 1, max: 10}}}
                                                        sx={{"& .MuiInputBase-input": {fontSize: "0.7rem", py: 0.4, px: 0.6}}}
                                                    />
                                                    <FormControl size="small" fullWidth>
                                                        <Select
                                                            value={item.lineStyle}
                                                            onChange={(e) => updateCard(card.id, c => ({
                                                                ...c,
                                                                seriesConfig: c.seriesConfig.map(s => s.key === item.key ? {...s, lineStyle: e.target.value} : s)
                                                            }))}
                                                            sx={{"& .MuiSelect-select": {fontSize: "0.7rem", py: 0.45, px: 0.8}}}
                                                        >
                                                            {lineStyleOptions.map(style => <MenuItem key={style} value={style}>{style}</MenuItem>)}
                                                        </Select>
                                                    </FormControl>
                                                </Box>
                                            </AccordionDetails>
                                        </Accordion>
                                    </Box>
                                );
                                })}
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );

    /*
     * ========================= CHART-ONLY MINI ÚTMUTATÓ =========================
     *
     * Hol jön az adat?
     * 1) A backendből a retrieveProjectStructure() tölti be a projektet:
     *    - projectDetails.hoursProgress      -> 1. vonaldiagram alapja
     *    - projectDetails.financialProgress  -> 2. vonaldiagram alapja
     *    - monthlyCosts                      -> oszlopdiagram alapja
     *    - projectDetails.totalEstimatedHours, draftInvoices -> "becsült" referencia-vonalak
     *
     * Melyik grafikon mit rajzol?
     * 2) 1. LineChart ("Teljesített Órák" + "Becsült Órák"):
     *    - Y: timeGraphData.y
     *    - X: timeGraphData.x (idő)
     * 3) 2. LineChart ("Kifizetett Számlák" + "Becsült Összeg"):
     *    - Y: financialGraphData.y
     *    - X: financialGraphData.x (idő)
     * 4) BarChart (havi bevétel/kiadás):
     *    - dataset a monthlyCosts-ból képzett objektumlista
     *    - series dataKey alapján olvas (pl. totalPaidExpense)
     *
     * Ha át akarod variálni, tipikusan ezt az 5 pontot módosítod:
     * A) Adattranszformáció: timeGraphData / financialGraphData / BarChart dataset
     * B) Sorok: LineChart.series és BarChart.series
     * C) Tengelyek: xAxis / yAxis (scaleType, formatter, min/max)
     * D) Stílus: color, strokeDasharray, showMark, height, margin
     * E) Feliratok: label, valueFormatter, legend viselkedés
     *
     * Gyors tipp:
     * - Ha "lapos" a görbe: külön yAxis kellhet (órák vs Ft), vagy normalizált értékek.
     * - Ha hiányos X tengely: ellenőrizd, hogy a date string parse-olható-e új Date()-tel.
     * ============================================================================
     */

    return (
        // A teljes oldal egy nagy "összetett editor + dashboard" felület:
        // - projekt kiválasztás
        // - több szerkeszthető blokk
        // - statisztikák/grafikonok
        // - összefoglaló
        <Paper sx={{padding: "10px", marginBottom: "30px"}}>
            <Box component="fieldset" sx={{border: "1px solid #e5e7eb", borderRadius: "12px", padding: "1rem", marginBottom: "20px"}}>
                <legend style={{fontWeight: "200", padding: "0 .5rem", color: "#575757"}}>Projekt</legend>

                <Grid container spacing={2} columns={20}>
                    <Grid size={{xs: 5}}>
                        <Autocomplete
                            fullWidth
                            options={filteredProjects}
                            getOptionLabel={(option) => "[" + option.identifier + "] " + option.name}
                            value={project || null}
                            onChange={(event, newValue) => {
                                setProject(newValue)
                                localStorage.setItem(LOCAL_STORAGE_NAME, JSON.stringify(newValue))
                            }}
                            renderInput={(params) => (
                                <TextField label={"Projekt neve"} {...params}/>
                            )}
                            sx={{flexGrow: 1}}
                        />
                    </Grid>
                    <Grid size={{xs: 5}}>

                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Stack spacing={0.5} sx={{width: "24px"}}>
                                <Tooltip title={"Előző"} placement={"right"}>
                                    <IconButton size="small" sx={{margin: "0px", padding: "0px"}} onClick={() => onNextProjectButtonClick(-1)}>
                                        <ExpandLess/>
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title={"Következő"} placement={"right"}>
                                    <IconButton size="small" sx={{margin: "0px", padding: "0px"}} onClick={() => onNextProjectButtonClick(+1)}>
                                        <ExpandMore/>
                                    </IconButton>
                                </Tooltip>
                            </Stack>

                            {relevanceFilter === "RELEVANTS_ONLY" && (
                                <Tooltip title="Csak aktuálisak">
                                    <Button variant="text" sx={{marginTop: "10px", padding: "0px"}} onClick={toggleRelevanceFilter}>
                                        <Star sx={{color: "gray", fontSize: "40px"}}/>
                                    </Button>
                                </Tooltip>
                            )}

                            {relevanceFilter === "SEMI_RELEVANTS_ONLY" && (
                                <Tooltip title="Csak fél-aktuálisak">
                                    <Button variant="text" sx={{marginTop: "10px", padding: "0px"}} onClick={toggleRelevanceFilter}>
                                        <StarHalf sx={{color: "gray", fontSize: "40px"}}/>
                                    </Button>
                                </Tooltip>
                            )}

                            {(relevanceFilter == null || relevanceFilter === "ALL") && (
                                <Tooltip title="Összes">
                                    <Button variant="text" sx={{marginTop: "10px", padding: "0px"}} onClick={toggleRelevanceFilter}>
                                        <StarOutline sx={{color: "gray", fontSize: "40px"}} size="large"/>
                                    </Button>
                                </Tooltip>
                            )}
                        </Stack>
                    </Grid>
                    <Grid size={{xs: 10}}></Grid>
                    <Grid size={{xs: 5}}>
                        {project?.id && <Button variant="contained" sx={{marginTop: "10px"}} onClick={() => navigate("/dashboard/projects/ " + project.id)}>Szerkesztés</Button>}
                        <Button color="success" variant="contained" sx={{marginTop: "10px", marginLeft: "10px"}} onClick={() => navigate("/dashboard/projects/create")}>Új projekt</Button>
                    </Grid>
                </Grid>

                {project?.id &&
                    <>
                        <Tooltip title={"Összes összecsukása"}>
                            <Button variant="text" component="span"
                                    onClick={() => collapseAll()}
                                    sx={{
                                        marginTop: "10px",
                                        marginRight: "10px",
                                        padding: "0px",
                                        minWidth: "0px",
                                        backgroundColor: "#b4b4b6",
                                        "&:hover": {backgroundColor: "#9c9ca0"},
                                    }}>
                                <UnfoldLess fontSize="large" sx={{color: "white", margin: "0px", padding: "5px"}}/>
                            </Button>
                        </Tooltip>
                    </>}
            </Box>

            {project?.id &&
                <>
                    <Box component="fieldset" sx={{border: (accordionStates?.openDetails ? "1px solid #e5e7eb" : "none"), borderRadius: "12px", padding: (accordionStates?.openDetails ? "1rem" : "0px calc(1rem + 1px)"), marginBottom: "20px"}}>
                        <legend style={{fontWeight: "200", padding: "0 .5rem", color: "#575757", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between"}}
                                onClick={() => flipState("openDetails")}>Részletek {accordionStates?.openDetails ? <ExpandLess/> : <ExpandMore/>}
                        </legend>

                        <Collapse in={accordionStates?.openDetails}>
                            <Grid container spacing={2} columns={20}>
                                <Grid size={{xs: 5}}>
                                    <FormControl fullWidth>
                                        <InputLabel id="currentStateLabel">Státusz</InputLabel>
                                        <Select
                                            variant="outlined"
                                            fullWidth
                                            name="currentState"
                                            label="Státusz"
                                            labelId="currentStateLabel"
                                            value={projectDetails?.currentState || ""}
                                            renderValue={value => value?.name}
                                            onChange={onProjectDetailsChange}
                                            sx={{textAlign: "left"}}
                                        >
                                            {projectStates.map((state, index) => (
                                                <MenuItem key={index} value={state}>{state.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid size={{xs: 2}}>
                                    <Tooltip title="Státusz beállítások">
                                        <Button variant="text" sx={{marginTop: "10px", padding: "0px"}} onClick={() => setOpenStateSettings(true)}>
                                            <Settings sx={{color: "gray", fontSize: "40px"}}/>
                                        </Button>
                                    </Tooltip>
                                    {project?.id && (
                                        <span style={{marginRight: "10px"}}>
                                            {projectDetails?.relevance === "RELEVANT" && (
                                                <Tooltip title="Aktuális">
                                                    <Button variant="text" sx={{marginTop: "10px", padding: "0px"}} onClick={() => toggleRelevance(project.id)}>
                                                        <Star sx={{color: "gold", fontSize: "40px"}}/>
                                                    </Button>
                                                </Tooltip>
                                            )}

                                            {projectDetails?.relevance === "SEMI_RELEVANT" && (
                                                <Tooltip title="Fél-aktuális">
                                                    <Button variant="text" sx={{marginTop: "10px", padding: "0px"}} onClick={() => toggleRelevance(project.id)}>
                                                        <StarHalf sx={{color: "gold", fontSize: "40px"}}/>
                                                    </Button>
                                                </Tooltip>
                                            )}

                                            {(projectDetails?.relevance == null || projectDetails?.relevance === "IRRELEVANT") && (
                                                <Tooltip title="Nem aktuális">
                                                    <Button variant="text" sx={{marginTop: "10px", padding: "0px"}} onClick={() => toggleRelevance(project.id)}>
                                                        <StarOutline sx={{color: "gold", fontSize: "40px"}} size="large"/>
                                                    </Button>
                                                </Tooltip>
                                            )}
                                        </span>
                                    )}
                                </Grid>

                                <Grid size={{xs: 5}}>
                                    <FormControlLabel control={<Checkbox
                                        variant="outlined"
                                        margin="normal"
                                        size="large"
                                        name="anythingToDo"
                                        onChange={onProjectDetailsCheckboxChange}
                                        checked={projectDetails?.anythingToDo}
                                    />} label="Van tennivaló"/>
                                </Grid>

                                <Grid size={{xs: 20}}>
                                    <TextField variant={"outlined"} multiline fullWidth label="Projekt leírása" name={"projectDescription"}
                                               value={projectDetails?.projectDescription || ""} minRows={3} maxRows={10} onChange={onProjectDetailsChange}/>
                                </Grid>
                            </Grid>

                            <div>
                                <Button variant="contained" sx={{marginTop: "10px"}} onClick={updateProjectDetails}>Mentés</Button>
                            </div>
                        </Collapse>
                    </Box>

                    <Box component="fieldset"
                         sx={{border: (accordionStates?.openProjectFiles ? "1px solid #e5e7eb" : "none"), borderRadius: "12px", padding: (accordionStates?.openProjectFiles ? "1rem" : "0px calc(1rem + 1px)"), marginBottom: "20px"}}>
                        <legend style={{fontWeight: "200", padding: "0 .5rem", color: "#575757", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between"}}
                                onClick={() => flipState("openProjectFiles")}>Projekt mellékletek {accordionStates?.openProjectFiles ? <ExpandLess/> : <ExpandMore/>}
                        </legend>

                        <Collapse in={accordionStates?.openProjectFiles}>
                            <input accept="*" id="file-upload-input" multiple type="file" style={{display: "none"}} onChange={handleFileChange}/>

                            <label htmlFor="file-upload-input">
                                <Button variant="text" component="span"
                                        sx={{
                                            margin: "10px 0px",
                                            padding: "0px",
                                            minWidth: "0px",
                                            backgroundColor: "#b4b4b6",
                                            "&:hover": {backgroundColor: "#9c9ca0"},
                                        }}>
                                    <AttachFile fontSize="large" sx={{color: "white", margin: 0, padding: "5px",}}/>
                                </Button>
                            </label>

                            {projectFiles.length > 0 && (
                                <List dense disablePadding sx={{mt: 1, border: "1px solid #e0e0e0", borderRadius: 1, overflow: "hidden"}}>
                                    {projectFiles.map((file, index) => (
                                        <ListItem key={index} disablePadding
                                                  sx={{
                                                      padding: "0px 10px",
                                                      display: "flex",
                                                      "& p": {lineHeight: "1.0"},
                                                      alignItems: "center",
                                                      "&:not(:last-child)": {borderBottom: "1px solid #f0f0f0"},
                                                  }}
                                                  secondaryAction={
                                                      <IconButton
                                                          edge="end"
                                                          size="small"
                                                          color="error"
                                                          onClick={() => deleteProjectFile(file.id)}
                                                          sx={{backgroundColor: "#b4b4b6", "&:hover": {backgroundColor: "#9c9ca0"}}}
                                                      >
                                                          <Delete fontSize="small" sx={{color: "white"}}/>
                                                      </IconButton>
                                                  }
                                        >
                                            <ListItemText
                                                slotProps={listItemTextProps}
                                                primary={file.name}
                                                secondary={file.createdAt}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}

                            {selectedProjectFiles.length > 0 && (
                                <List dense disablePadding sx={{mt: 1, border: "1px solid #e0e0e0", borderRadius: 1, overflow: "hidden", marginBottom: "10px"}}>
                                    {selectedProjectFiles.map((file, index) => (
                                        <ListItem key={index} disablePadding
                                                  sx={{
                                                      padding: "0px 10px",
                                                      display: "flex",
                                                      "& p": {lineHeight: "1.0"},
                                                      alignItems: "center",
                                                      backgroundColor: "#fff9b8",
                                                      "&:not(:last-child)": {borderBottom: "1px solid #f0f0f0"},
                                                  }}
                                                  secondaryAction={
                                                      <IconButton
                                                          edge="end"
                                                          size="small"
                                                          color="error"
                                                          onClick={() => handleRemoveFile(index)}
                                                          sx={{backgroundColor: "#b4b4b6", "&:hover": {backgroundColor: "#9c9ca0"}}}
                                                      >
                                                          <Close fontSize="small" sx={{color: "white"}}/>
                                                      </IconButton>
                                                  }
                                        >
                                            <ListItemText
                                                slotProps={listItemTextProps}
                                                primary={file.name}
                                                secondary={`${(file.size / 1024).toFixed(1)} KB`}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                            <Button variant="contained" sx={{marginLeft: "10px"}} onClick={() => uploadProjectFiles()}>Mentés</Button>
                        </Collapse>
                    </Box>

                    <Box component="fieldset" sx={{border: (accordionStates?.openStats ? "1px solid #e5e7eb" : "none"), borderRadius: "12px", padding: (accordionStates?.openStats ? "1rem" : "0px calc(1rem + 1px)"), marginBottom: "20px"}}>
                        <legend style={{fontWeight: "200", padding: "0 .5rem", color: "#575757", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between"}}
                                onClick={() => flipState("openStats")}>Statisztika {accordionStates?.openStats ? <ExpandLess/> : <ExpandMore/>}
                        </legend>
                        <Collapse in={accordionStates?.openStats}>
                            <Box sx={{display: "flex", gap: 1, alignItems: "center", marginBottom: "10px"}}>
                                <FormControl size="small" sx={{minWidth: 220}}>
                                    <InputLabel id="new-chart-template-label">Új grafikon sablon</InputLabel>
                                    <Select
                                        labelId="new-chart-template-label"
                                        value={newChartTemplate}
                                        label="Új grafikon sablon"
                                        onChange={(e) => setNewChartTemplate(e.target.value)}
                                    >
                                        <MenuItem value="hoursLine">Óra trend</MenuItem>
                                        <MenuItem value="financialLine">Pénzügyi trend</MenuItem>
                                        <MenuItem value="monthlyBar">Havi bevétel/kiadás</MenuItem>
                                    </Select>
                                </FormControl>
                                <Button variant="contained" size="small" onClick={addChartCard} sx={{textTransform: "none"}}>
                                    Grafikon hozzáadása
                                </Button>
                            </Box>

                            {chartCards.map((card) => {
                                const cardData = cardDataById[card.id];
                                const lineSeries = getLineSeriesForCard(card, cardData);
                                const barSeries = getBarSeriesForCard(card, cardData);
                                const pieSeries = getPieSeriesForCard(card, cardData);

                                return (
                                    <Accordion key={card.id} disableGutters expanded={!!card.open}
                                               onChange={() => updateCard(card.id, c => ({...c, open: !c.open}))} sx={{
                                        marginBottom: "10px",
                                        border: "1px solid rgba(25, 118, 210, 0.18)",
                                        borderRadius: "10px !important",
                                        overflow: "hidden",
                                        boxShadow: "0 4px 12px rgba(9, 30, 66, 0.05)"
                                    }}>
                                        <AccordionSummary expandIcon={<ExpandMore/>} sx={{minHeight: "40px !important", backgroundColor: "#f6f9fd", "& .MuiTypography-root": {fontWeight: 700, fontSize: "0.92rem"}}}>
                                            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", pr: 1}}>
                                                <Typography sx={{fontWeight: 700, fontSize: "0.92rem"}}>{card.title}</Typography>
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    variant="text"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        removeCard(card.id);
                                                    }}
                                                    sx={{textTransform: "none", minWidth: "0", padding: "2px 8px"}}
                                                >
                                                    Eltávolítás
                                                </Button>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Accordion disableGutters expanded={!!card.openSetup}
                                                       onChange={() => updateCard(card.id, c => ({...c, openSetup: !c.openSetup}))}
                                                       sx={{marginBottom: "10px", border: "1px dashed rgba(25, 118, 210, 0.22)", borderRadius: "8px"}}>
                                                <AccordionSummary expandIcon={<ExpandMore/>} sx={{minHeight: "36px !important", backgroundColor: "#f9fbff"}}>
                                                    <Box sx={{width: "100%"}}>
                                                        <Typography sx={{fontSize: "0.82rem", fontWeight: 700, color: "#1f3d5a"}}>Setup</Typography>
                                                        {renderSetupSummary(card.seriesConfig, cardData)}
                                                    </Box>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    {renderSetupPanel(card)}
                                                </AccordionDetails>
                                            </Accordion>

                                            {card.renderType === "pie" ? (
                                                <EnginePieChart
                                                    title={card.title}
                                                    height={360}
                                                    series={pieSeries}
                                                    styleConfig={card.style}
                                                />
                                            ) : card.renderType === "bar" ? (
                                                <EngineBarChart
                                                    title={card.title}
                                                    height={360}
                                                    labels={cardData.monthlyLabels}
                                                    series={barSeries}
                                                    styleConfig={card.style}
                                                />
                                            ) : (
                                                <EngineLineChart
                                                    title={card.title}
                                                    height={360}
                                                    series={lineSeries}
                                                    styleConfig={card.style}
                                                />
                                            )}
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            })}
                        </Collapse>
                    </Box>

                    <Box component="fieldset"
                         sx={{border: (accordionStates?.openProductProjects ? "1px solid #e5e7eb" : "none"), borderRadius: "12px", padding: (accordionStates?.openProductProjects ? "1rem" : "0px calc(1rem + 1px)"), marginBottom: "20px"}}>
                        <legend style={{fontWeight: "200", padding: "0 .5rem", color: "#575757", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between"}}
                                onClick={() => flipState("openProductProjects")}>Termék projektek {accordionStates?.openProductProjects ? <ExpandLess/> : <ExpandMore/>}
                        </legend>

                        <Collapse in={accordionStates?.openProductProjects}>
                            <Grid container spacing={1} columns={20}>
                                {selectedProductProjects.map((selected, index) => (
                                    <React.Fragment key={index}>
                                        <Grid size={{xs: 10}} sx={{display: "flex", alignItems: "center", gap: "8px"}}>
                                            <Autocomplete
                                                fullWidth
                                                options={productProjects}
                                                getOptionLabel={(option) => option?.name || ""}
                                                value={selected || null}
                                                onChange={(event, newValue) => handleProductProjectsChange(index, newValue)}
                                                renderInput={(params) => (
                                                    <TextField label={`${index + 1}. Termék projekt`} {...params} />
                                                )}
                                                sx={{flexGrow: 1}}
                                            />
                                            <Tooltip title="Tovább a Termék Projektre" placement="right">
                                                <Button variant="text" component="span" onClick={() => navigate("/dashboard/product-projects/" + selected?.id)}
                                                        sx={{margin: "10px 0px", padding: "0px", minWidth: "0px", backgroundColor: "#b4b4b6", "&:hover": {backgroundColor: "#9c9ca0"},}}>
                                                    <Launch fontSize="large" sx={{color: "white", margin: 0, padding: "5px",}}/>
                                                </Button>
                                            </Tooltip>
                                        </Grid>
                                        <Grid size={{xs: 10}} sx={{display: "flex", alignItems: "center"}}>
                                            <TextField
                                                fullWidth
                                                name="amount"
                                                value={selected?.amount || ""}
                                                onChange={e => onProductProjectChange(e, index)}
                                                label="Mennyiség"
                                                variant="outlined"
                                                type="number"
                                            />
                                            <IconButton color="error" onClick={() => handleRemoveProductProject(index)}
                                                        sx={{marginLeft: "10px", backgroundColor: "#b4b4b6", "&:hover": {backgroundColor: "#9c9ca0"}}}>
                                                <Remove sx={{color: "white"}}/>
                                            </IconButton>
                                        </Grid>
                                        <Grid size={{xs: 20}} sx={{marginBottom: "20px"}}>
                                            <span><b>Teljes Beszerzési Ár: </b>{parseToHUF(selected?.totalPurchaseCost)} | </span>
                                            <span><b>Teljes Eladási Ár:  </b>{parseToHUF(selected?.totalSellingPrice)} | </span>
                                            <span><b>Teljes Belső Munkadíj:  </b>{parseToHUF(selected?.totalInternalLaborCost)} | </span>
                                            <span><b>Teljes Külső Munkadíj:  </b>{parseToHUF(selected?.totalExternalLaborPrice)} | </span>
                                            <span><b>Összesen:  </b>{parseToHUF(selected?.total)}</span>
                                        </Grid>
                                    </React.Fragment>
                                ))}
                            </Grid>

                            <Button
                                variant="text"
                                sx={{
                                    margin: "10px 0px",
                                    padding: "0px",
                                    minWidth: "0px",
                                    backgroundColor: "#b4b4b6",
                                    "&:hover": {backgroundColor: "#9c9ca0"}
                                }}
                                onClick={handleAddProductProject}
                            >
                                <Add fontSize="large" sx={{color: "white"}}/>
                            </Button>

                            <TextField variant={"outlined"} multiline fullWidth label="Termék projekt megjegyzés" name={"productProjectsDescription"}
                                       value={projectDetails?.productProjectsDescription || ""} minRows={3} maxRows={10} onChange={onProjectDetailsChange}/>

                            <div>
                                <Button variant="contained" sx={{marginTop: "10px"}} onClick={updateProductProjects}>Mentés</Button>
                                <Button variant="contained" color={"success"} sx={{marginTop: "10px", marginLeft: "10px"}} onClick={saveAndNavigateToProductProjects}>Új Termék Projekt</Button>
                            </div>
                        </Collapse>
                    </Box>

                    <Box component="fieldset"
                         sx={{border: (accordionStates?.openProjectTasks ? "1px solid #e5e7eb" : "none"), borderRadius: "12px", padding: (accordionStates?.openProjectTasks ? "1rem" : "0px calc(1rem + 1px)"), marginBottom: "20px"}}>
                        <legend style={{fontWeight: "200", padding: "0 .5rem", color: "#575757", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between"}}
                                onClick={() => flipState("openProjectTasks")}>Fejlécek {accordionStates?.openProjectTasks ? <ExpandLess/> : <ExpandMore/>}
                        </legend>

                        <Collapse in={accordionStates?.openProjectTasks}>
                            <Grid container spacing={1} sx={{marginBottom: "8px"}} columns={20}>
                                {selectedProjectTasks.map((selected, index) => (
                                    <>
                                        <Grid key={index} sx={{display: "flex", alignItems: "center"}} size={{xs: 20}}>
                                            <Autocomplete
                                                fullWidth
                                                freeSolo={true}
                                                options={projectTasks}
                                                getOptionLabel={(option) => option?.description || ""}
                                                value={selected || ""}
                                                onChange={(event, newValue) => handleProjectTaskChange(index, newValue)}
                                                onInputChange={(event, newInputValue) => {
                                                    handleProjectTaskChange(index, {
                                                        ...selectedProjectTasks[index],
                                                        description: newInputValue,
                                                    });
                                                }}
                                                renderInput={(params) => (
                                                    <TextField label={`${index + 1}. Fejléc`} {...params} />
                                                )}
                                                sx={{flexGrow: 1}}
                                            />
                                            <IconButton
                                                color="error"
                                                onClick={() => handleRemoveProjectTask(index)}
                                                sx={{marginLeft: "10px", backgroundColor: "#b4b4b6", "&:hover": {backgroundColor: "#9c9ca0"}}}
                                            >
                                                <Remove sx={{color: "white"}}/>
                                            </IconButton>


                                        </Grid>

                                        {(selected?.tasks?.length == null || selected.tasks.length === 0) &&
                                            <Grid size={{xs: 1}}>
                                                <Tooltip title="Feladat hozzáadása" placement="right">
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleAddTaskToProjectTask(index, 0)}
                                                        sx={{marginLeft: "10px", marginTop: "0px", backgroundColor: "#b4b4b6", "&:hover": {backgroundColor: "#9c9ca0"}}}
                                                    >
                                                        <Add sx={{color: "white"}} fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>
                                            </Grid>}

                                        {selected?.tasks?.map((task, taskIndex) => (
                                            <React.Fragment key={index + "-" + taskIndex}>
                                                <Grid size={{xs: 1}} sx={{textAlign: "center"}}>
                                                    <Tooltip title="Tovább a feladatra" placement="left">
                                                        <Button variant="text" component="span" onClick={() => navigate("/dashboard/tasks/ " + task.id)}
                                                                sx={{margin: "16px 0px 0px 0px", padding: "0px", minWidth: "0px", backgroundColor: "#b4b4b6", "&:hover": {backgroundColor: "#9c9ca0"},}}>
                                                            <Launch fontSize="large" sx={{color: "white", margin: 0, padding: "5px",}}/>
                                                        </Button>
                                                    </Tooltip>
                                                </Grid>

                                                <Grid size={{xs: 5}}>
                                                    <TextField variant={"outlined"} fullWidth label="Feladat leírása" name={"description"}
                                                               value={task?.description || ""} onChange={(e) => onTaskChange(index, taskIndex, e.target.name, e.target.value)} sx={{marginTop: "8px"}}/>
                                                </Grid>

                                                <Grid size={{xs: 2}}>
                                                    <FormControl sx={{
                                                        m: 1,
                                                        minWidth: 120,
                                                        marginLeft: "0px"
                                                    }} fullWidth>
                                                        <InputLabel id="priority-label">Prioritás</InputLabel>
                                                        <Select
                                                            variant="outlined"
                                                            fullWidth
                                                            labelId="priority-label"
                                                            name="priority"
                                                            label="Prioritás"
                                                            value={task.priority || "NORMAL"}
                                                            defaultValue={"NORMAL"}
                                                            onChange={(e) => onTaskChange(index, taskIndex, e.target.name, e.target.value)}
                                                            sx={{textAlign: "left"}}
                                                        >
                                                            <MenuItem value={"EXTRA_IMPORTANT"}>Extra fontos</MenuItem>
                                                            <MenuItem value={"IMPORTANT"}>Fontos</MenuItem>
                                                            <MenuItem value={"NORMAL"}>Normál</MenuItem>
                                                            <MenuItem value={"NOT_IMPORTANT"}>Nem fontos</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>

                                                <Grid size={{xs: 2}}>
                                                    <FormControl sx={{
                                                        m: 1,
                                                        minWidth: 120,
                                                        marginLeft: "0px"
                                                    }} fullWidth>
                                                        <InputLabel id="status-label">Státusz</InputLabel>
                                                        <Select
                                                            variant="outlined"
                                                            fullWidth
                                                            labelId="status-label"
                                                            name="status"
                                                            label="Státusz"
                                                            value={task.status || "NOT_STARTED"}
                                                            defaultValue={"NOT_STARTED"}
                                                            onChange={(e) => onTaskChange(index, taskIndex, e.target.name, e.target.value)}
                                                            sx={{textAlign: "left"}}
                                                        >
                                                            <MenuItem value={"NOT_STARTED"}>Nincs elkezdve</MenuItem>
                                                            <MenuItem value={"IN_PROGRESS"}>Folyamatban</MenuItem>
                                                            <MenuItem value={"BLOCKED"}>Elakadva</MenuItem>
                                                            <MenuItem value={"OVERDUE"}>Csúszásban</MenuItem>
                                                            <MenuItem value={"DONE"}>Befejezve</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>

                                                <Grid size={{xs: 2}}>
                                                    <TextField
                                                        variant="outlined"
                                                        margin="normal"
                                                        fullWidth
                                                        type="number"
                                                        name="estimatedHours"
                                                        label="Becsült órák"
                                                        autoComplete="off"
                                                        onChange={(e) => onTaskChange(index, taskIndex, e.target.name, e.target.value)}
                                                        sx={{marginTop: "8px"}}
                                                        value={task.estimatedHours || ""}
                                                    />
                                                </Grid>

                                                <Grid size={{xs: 6}}>
                                                    <FormControl sx={{m: 1, minWidth: 120, marginTop: "8px", marginLeft: "0px"}} fullWidth>
                                                        <InputLabel id="users-label">Dolgozók</InputLabel>
                                                        <Select
                                                            labelId="users-label"
                                                            multiple
                                                            value={task.users || []}
                                                            onChange={(e) => onUsersChange(index, taskIndex, e)}
                                                            label="Dolgozók"
                                                            variant="outlined"
                                                            input={<OutlinedInput id="select-multiple-chip"
                                                                                  label="Dolgozók"/>}
                                                            renderValue={(selected) => (
                                                                <Box sx={{
                                                                    display: 'flex',
                                                                    flexWrap: 'wrap',
                                                                    gap: 1.0,
                                                                    margin: "-5px"
                                                                }}>
                                                                    {selected.map((value, i) => (
                                                                        <Chip key={i} label={value.name}/>
                                                                    ))}
                                                                </Box>
                                                            )}
                                                            MenuProps={MenuProps}
                                                        >
                                                            {users.map((user, i) => (
                                                                <MenuItem
                                                                    key={index + "-" + taskIndex + "-" + i}
                                                                    value={user}
                                                                    style={getStyles(user.name, task.users)}
                                                                >
                                                                    {user.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>

                                                <Grid size={{xs: 2}}>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleRemoveTaskOfProjectTask(index, taskIndex)}
                                                        sx={{marginLeft: "10px", marginTop: "20px", backgroundColor: "#b4b4b6", "&:hover": {backgroundColor: "#9c9ca0"}}}
                                                    >
                                                        <Remove sx={{color: "white"}} fontSize="small"/>
                                                    </IconButton>

                                                    {taskIndex === selected?.tasks?.length - 1 &&
                                                        <Tooltip title="Feladat hozzáadása" placement="right">
                                                            <IconButton
                                                                color="error"
                                                                onClick={() => handleAddTaskToProjectTask(index, taskIndex)}
                                                                sx={{marginLeft: "10px", marginTop: "20px", backgroundColor: "#b4b4b6", "&:hover": {backgroundColor: "#9c9ca0"}}}
                                                            >
                                                                <Add sx={{color: "white"}} fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>}
                                                </Grid>
                                            </React.Fragment>
                                        ))}
                                    </>
                                ))}
                            </Grid>

                            <Tooltip title="Fejléc hozzáadása" placement="right">
                                <Button variant="text" sx={{
                                    margin: "10px 0px",
                                    padding: "0px",
                                    minWidth: "0px",
                                    backgroundColor: "#b4b4b6",
                                    "&:hover": {backgroundColor: "#9c9ca0"}
                                }} onClick={handleAddProjectTask}>
                                    <Add fontSize="large" color="action" sx={{
                                        color: "white",
                                        marginTop: "0px",
                                        marginLeft: "0px"
                                    }}/>
                                </Button>
                            </Tooltip>

                            <TextField variant={"outlined"} multiline fullWidth label="Fejléc megjegyzés" name={"projectTasksDescription"}
                                       value={projectDetails?.projectTasksDescription || ""} minRows={3} maxRows={10} onChange={onProjectDetailsChange}/>

                            <div>
                                <Button variant="contained" sx={{marginTop: "10px"}} onClick={() => updateProjectTasks()}>Mentés</Button>
                            </div>
                        </Collapse>
                    </Box>

                    <Box component="fieldset"
                         sx={{border: (accordionStates?.openDraftInvoices ? "1px solid #e5e7eb" : "none"), borderRadius: "12px", padding: (accordionStates?.openDraftInvoices ? "1rem" : "0px calc(1rem + 1px)"), marginBottom: "20px"}}>
                        <legend style={{fontWeight: "200", padding: "0 .5rem", color: "#575757", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between"}}
                                onClick={() => flipState("openDraftInvoices")}>Számla Tervek {accordionStates?.openDraftInvoices ? <ExpandLess/> : <ExpandMore/>}
                        </legend>
                        <Collapse in={accordionStates?.openDraftInvoices}>
                            <Grid container spacing={1} columns={20}>
                                {draftInvoices.map((invoice, index) => (
                                    <React.Fragment key={index}>
                                        <Grid size={{xs: 4}} sx={{display: "flex"}}>
                                            <FormControl fullWidth>
                                                <InputLabel id="invoice-type-label">Számla típusa</InputLabel>
                                                <Select
                                                    variant="outlined"
                                                    labelId="invoice-type-label"
                                                    name="invoiceType"
                                                    value={invoice?.invoiceType || ""}
                                                    onChange={(e) => onDraftInvoiceChange(e, index)}
                                                    label="Számla típusa"
                                                    sx={{textAlign: "left"}}
                                                >
                                                    <MenuItem value={"INVOICE"}>Számla</MenuItem>
                                                    <MenuItem value={"ADVANCE_INVOICE"}>Előlegszámla</MenuItem>
                                                    <MenuItem value={"FEE_REQUEST"}>Díjbekérő</MenuItem>
                                                    <MenuItem value={"RECEIPT"}>Nyugta</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid size={{xs: 4}}>
                                            <TextField name="amount" value={invoice?.amount || ""} onChange={(e) => onDraftInvoiceChange(e, index)} fullWidth label="Összeg (HUF)"/>
                                        </Grid>
                                        <Grid size={{xs: 4}}>
                                            <TextField name="comment" value={invoice?.comment || ""} onChange={(e) => onDraftInvoiceChange(e, index)} fullWidth label="Megjegyzés"/>
                                        </Grid>
                                        <Grid size={{xs: 8}}>
                                            <IconButton color="error" onClick={() => handleRemoveDraftInvoice(index)}
                                                        sx={{marginLeft: "10px", marginTop: "10px", backgroundColor: "#b4b4b6", "&:hover": {backgroundColor: "#9c9ca0"}}}>
                                                <Remove sx={{color: "white"}}/>
                                            </IconButton>
                                        </Grid>
                                    </React.Fragment>
                                ))}
                            </Grid>

                            <Button variant="text" sx={{margin: "10px 0px", padding: "0px", minWidth: "0px", backgroundColor: "#b4b4b6", "&:hover": {backgroundColor: "#9c9ca0"}}}
                                    onClick={handleAddDraftInvoice}>
                                <Add fontSize="large" sx={{color: "white"}}/>
                            </Button>

                            <TextField variant={"outlined"} fullWidth label="Egyéb költségek (Ft)" name={"otherPlannedExpenses"}
                                       value={projectDetails?.otherPlannedExpenses || ""} onChange={onProjectDetailsChange} sx={{marginBottom: "10px"}}/>
                            <TextField variant={"outlined"} multiline fullWidth label="Számla tervek megjegyzés" name={"draftInvoicesDescription"}
                                       value={projectDetails?.draftInvoicesDescription || ""} minRows={3} maxRows={10} onChange={onProjectDetailsChange}/>

                            <div>
                                <Button variant="contained" sx={{marginTop: "10px"}} onClick={updateDraftInvoices}>Mentés</Button>
                            </div>
                        </Collapse>
                    </Box>

                    <Box component="fieldset" sx={{border: (accordionStates?.openInvoices ? "1px solid #e5e7eb" : "none"), borderRadius: "12px", padding: (accordionStates?.openInvoices ? "1rem" : "0px calc(1rem + 1px)"), marginBottom: "20px"}}>
                        <legend style={{fontWeight: "200", padding: "0 .5rem", color: "#575757", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between"}}
                                onClick={() => flipState("openInvoices")}>Számlák {accordionStates?.openInvoices ? <ExpandLess/> : <ExpandMore/>}
                        </legend>
                        <Collapse in={accordionStates?.openInvoices}>
                            <Grid container spacing={1} columns={20}>
                                {invoices.map((invoice, index) => (
                                    <React.Fragment key={index}>
                                        <Grid size={{xs: 4}} sx={{display: "flex"}}>
                                            <FormControl fullWidth>
                                                <InputLabel id="invoice-type-label">Számla típusa</InputLabel>
                                                <Select
                                                    variant="outlined"
                                                    labelId="invoice-type-label"
                                                    name="invoiceType"
                                                    value={invoice?.invoiceType || ""}
                                                    label="Számla típusa"
                                                    sx={{textAlign: "left"}}
                                                >
                                                    <MenuItem value={"INVOICE"}>Számla</MenuItem>
                                                    <MenuItem value={"ADVANCE_INVOICE"}>Előlegszámla</MenuItem>
                                                    <MenuItem value={"FEE_REQUEST"}>Díjbekérő</MenuItem>
                                                    <MenuItem value={"RECEIPT"}>Nyugta</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid size={{xs: 4}}>
                                            <TextField name="invoiceName" value={invoice?.name || ""} fullWidth label="Számla neve"/>
                                        </Grid>
                                        <Grid size={{xs: 4}}>
                                            <TextField name="invoiceAmount" value={invoice?.amount || ""} fullWidth label="Összeg (HUF)"/>
                                        </Grid>
                                        <Grid size={{xs: 4}}>
                                            <TextField
                                                name="invoiceDate"
                                                fullWidth
                                                label="Teljesítés Dátuma"
                                                variant="outlined"
                                                value={invoice?.dateOfInvoice || ""}
                                                type="date"
                                                slotProps={{inputLabel: {shrink: true}}}
                                            />
                                        </Grid>
                                        <Grid size={{xs: 3}}>
                                            <Tooltip title="Szerkesztés" placement="right">
                                                <Button variant="text" component="span" onClick={() => navigate("/dashboard/simple-invoices/ " + invoice.id)}
                                                        sx={{margin: "10px 0px", padding: "0px", minWidth: "0px", backgroundColor: "#b4b4b6", "&:hover": {backgroundColor: "#9c9ca0"},}}>
                                                    <Launch fontSize="large" sx={{color: "white", margin: 0, padding: "5px",}}/>
                                                </Button>
                                            </Tooltip>
                                        </Grid>
                                    </React.Fragment>
                                ))}
                            </Grid>

                            <div>
                                <Button variant="contained" color={"success"} sx={{marginTop: "10px"}} onClick={() => navigate("/dashboard/simple-invoices/create")}>Új Számla</Button>
                            </div>

                        </Collapse>
                    </Box>

                    <Box component="fieldset" sx={{border: "1px solid #e5e7eb", borderRadius: "12px", padding: "1rem"}}>
                        <legend style={{fontWeight: "200", padding: "0 .5rem", color: "#575757"}}>Összefoglaló</legend>
                        <b>Projekt</b>
                        <ul>
                            <li>Név: {project?.name}</li>
                            <li>Projektszám: {project?.identifier}</li>
                            <li>Összérték: {parseToHUF(draftInvoices.map(pt => pt?.amount || 0).reduce((a, b) => a + b, 0) + parseInt(projectDetails?.otherPlannedExpenses))}</li>
                            <li>Készültség: ? %</li>
                            <li>Státusz: {projectDetails.currentState?.name}</li>
                        </ul>

                        <b>Megrendelés</b>
                        <ul></ul>
                        <b>Árajánlat</b>
                        <ul></ul>
                        <b>Termék projektek</b>
                        <ul>
                            {selectedProductProjects?.map((selected, index) => (
                                <li key={index}>{index + 1}. {selected?.name} - Összesen: {parseToHUF(selected?.total)}</li>
                            ))}
                        </ul>

                        <b>Fejlécek</b>
                        <ul>
                            {selectedProjectTasks?.map((selected, index) => (
                                <li key={index}>{index + 1}. {selected?.description}</li>
                            ))}
                        </ul>

                        <b>Tervezett számlák</b>
                        <ul>
                            {draftInvoices?.map((selected, index) => (
                                <li key={index}>{index + 1}. {INVOICE_TYPES[selected?.invoiceType]} | Nettó: {selected?.amount} HUF | Megjegyzés: {selected?.comment}</li>
                            ))}
                        </ul>

                        <b>Valós számlák</b>
                        <ul>
                            {invoices?.map((selected, index) => (
                                <li key={index}>{index + 1}. {INVOICE_TYPES[selected?.invoiceType]} | Nettó: {parseToHUF(selected?.amount / (1 + selected.vat / 100))} | Bruttó: {parseToHUF(selected?.amount)}</li>
                            ))}
                        </ul>

                    </Box>
                </>}

            {/* Projekt státusz opciók admin modal */}
            <Dialog open={openStateSettings} onClose={onSettingsClose} maxWidth="false" sx={{width: "50%", margin: "auto"}}>
                <form onSubmit={updateProjectStates}>
                    <DialogTitle sx={{
                        display: 'flex',
                        alignItems: 'top',
                        justifyContent: 'space-between',
                        bgcolor: '#1976d2;',
                        color: '#fff'
                    }}>
                        <Typography variant="h6">Projekt státusz lehetőségek</Typography>
                    </DialogTitle>
                    <DialogContent sx={{bgcolor: '#ededed'}}>
                        <DialogContentText sx={{mb: 1, paddingTop: "15px", color: "black"}}>
                            <div style={{overflowY: 'scroll', maxHeight: '400px'}}>

                                <Grid container columns={20} spacing={1}>
                                    {projectStates?.map((state, index) => (
                                        <React.Fragment key={index}>
                                            <Grid size={{xs: 18}}>
                                                <TextField name="name" required onChange={e => onProjectStateChange(e, index)} fullWidth value={state?.name}
                                                           sx={{'& input ': {padding: "4px 5px !important"}}}
                                                           autoComplete="off"></TextField>
                                            </Grid>
                                            <Grid size={{xs: 2}}>
                                                <Tooltip title={"Eltávolítás"} placement={"left"}>
                                                    <IconButton sx={{marginTop: "3px", marginRight: "8px", padding: "0px", backgroundColor: "gray"}} onClick={() => removeProjectStateAt(index)}>
                                                        <Remove sx={{color: "white"}}/>
                                                    </IconButton>
                                                </Tooltip>
                                                {index === projectStates?.length - 1 &&
                                                    <Tooltip title={"Hozzáadás"} placement={"right"}>
                                                        <IconButton sx={{marginTop: "3px", padding: "0px", backgroundColor: "gray"}} onClick={() => addProjectState()}>
                                                            <Add sx={{color: "white"}}/>
                                                        </IconButton>
                                                    </Tooltip>
                                                }
                                            </Grid>
                                        </React.Fragment>
                                    ))}
                                </Grid>

                            </div>
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{p: 2, bgcolor: '#ededed', justifyContent: 'space-between'}}>
                        <Button type="submit" variant="contained">Mentés</Button>
                        <div>
                            <Button onClick={retrieveProjectStates} disabled={!resetStatesButtonEnabled} variant="outlined" sx={{marginRight: "8px"}}>Visszaállítás</Button>
                            <Button onClick={onSettingsClose} variant="outlined">Bezárás</Button>
                        </div>
                    </DialogActions>
                </form>
            </Dialog>

        </Paper>
    );
};

export default ProjectStructure;
